/**
 * Pure progress summary for the Rollout tab (Task D5).
 *
 * This is deliberately a THIN wrapper. `classifyDeviceRelease` /
 * `countFleetReleaseStatuses` in `pos/fleet/fleetRows.ts` (Task D4) are the
 * ONE definition of "is this device on target" in this product — the Fleet
 * table in `/pos-config` counts devices with exactly that function. A second,
 * separate implementation here would eventually disagree with the Fleet
 * table about the same devices: that is the seam shape (a value computed
 * twice, two ways) that produced six defects earlier in this project. So this
 * module calls `countFleetReleaseStatuses` directly and only adds what the
 * hub needs on top: labels, descriptions, and the pre-filtered drill-in link
 * per bucket — it computes nothing about device state itself.
 *
 * PURITY CONTRACT: no network, no React, no `Date.now()` unless the caller
 * declines to supply `now` (that default lives in fleetRows.ts already).
 */
import {
  classifyDeviceRelease as _classifyDeviceRelease, // re-exported below for callers that want it directly
  countFleetReleaseStatuses,
  FLEET_RELEASE_STATUSES,
  type ClassifiableDevice,
  type FleetReleaseStatus,
  type FleetRowOptions,
} from '../pos/fleet/fleetRows';

export const classifyDeviceRelease = _classifyDeviceRelease;
export type { ClassifiableDevice, FleetReleaseStatus, FleetRowOptions };

/**
 * Fixed display order. The spec's example shows three buckets ("14 of 20 on
 * target · 3 pinned · 1 stale") but the classifier has FIVE — the ruling is
 * to display all five (never silently drop one), because the property that
 * makes these numbers trustworthy is that they sum to the fleet total.
 * `on-target` leads as the "good" state; `stale` and `unknown` are grouped
 * at the end as the two "we can't vouch for this" states.
 */
export const ROLLOUT_BUCKET_ORDER: readonly FleetReleaseStatus[] = [
  'on-target',
  'behind',
  'pinned',
  'stale',
  'unknown',
];

// Bucket order must be exactly the classifier's exhaustive set — a mismatch
// here would silently drop or duplicate a bucket, which is the one thing
// this module exists to prevent.
if (ROLLOUT_BUCKET_ORDER.length !== FLEET_RELEASE_STATUSES.length) {
  throw new Error('rolloutProgress: ROLLOUT_BUCKET_ORDER is out of sync with FLEET_RELEASE_STATUSES');
}

const BUCKET_META: Record<FleetReleaseStatus, { label: string; description: string }> = {
  'on-target': {
    label: 'On target',
    description: 'Running the version resolved for it (its ring, unpinned).',
  },
  behind: {
    label: 'Behind',
    description: 'Reporting a live version that differs from its resolved target.',
  },
  pinned: {
    label: 'Pinned',
    description: 'Held on a specific version by an operator — shown separately from "on target" on purpose.',
  },
  stale: {
    label: 'Stale',
    description: 'Never checked in, or has not checked in recently enough to vouch for.',
  },
  unknown: {
    label: 'Unknown',
    description: 'No resolved target yet, or no live version to compare — never assumed on target.',
  },
};

export interface RolloutBucket {
  status: FleetReleaseStatus;
  label: string;
  description: string;
  count: number;
  /** Pre-filtered drill-in: the ONE place a device is managed. Never render a device list here. */
  href: string;
}

export interface RolloutProgressResult {
  total: number;
  buckets: RolloutBucket[];
  /** True when there are no devices at all — the tab renders "no devices", never "0 of 0". */
  isEmpty: boolean;
}

export function rolloutProgress(
  devices: readonly ClassifiableDevice[],
  options: FleetRowOptions = {},
): RolloutProgressResult {
  const counts = countFleetReleaseStatuses(devices, options);
  const buckets: RolloutBucket[] = ROLLOUT_BUCKET_ORDER.map((status) => ({
    status,
    label: BUCKET_META[status].label,
    description: BUCKET_META[status].description,
    count: counts[status],
    href: `/pos-config?tab=fleet&status=${status}`,
  }));
  return { total: counts.total, buckets, isEmpty: counts.total === 0 };
}
